import {
	ActionMenuWrapper,
	Button,
	ButtonGroup,
	Divider,
	DropdownMenuItem,
	FileUpload,
	Loading,
	Table,
	TableColumn,
	TableRow,
} from '@aon/aon-tws-common-ui';
import _ from 'lodash';
import * as React from 'react';
import styled from 'styled-components';
import ContentConstants from '../../../contentConstants';
import {
	DistributionFileUploadType,
	DistributionListUnderValidation,
	DistributionType,
	DistributionUploadStatus,
	ExternalApplicationType,
	IAction,
	IDistributionContactActionCreators,
	IDistributionFile,
	ISurveyParticipantActionCreators,
	IValidationErrorCollection,
} from '../../../models';
import { AccessCodeType } from '../../../models/AccessCodeType';
import { SsoAttribute } from '../../../models/SsoAttribute';
import { IDistributionContactState } from '../../../store/DistributionContactStore';
import { ISurveyParticipantState } from '../../../store/SurveyParticipantStore';
import { ValidationErrorHelper } from '../../../utilities';
import { DistributionHelper } from '../../../utilities/DistributionHelper';
import { Col3Col9, LoadingWrapper, WarningModal } from '../../common';
import { ErrorMessageLabel } from '../SharedComponents';
import { AccessCodes } from '../accessCodes/AccessCodes';
import { AppendDistributionModal } from './AppendDistributionModal';
import { DistributionContactsModal } from './DistributionContactsModal';
import { DistributionUploadStatusMessage } from './DistributionUploadStatusMessage';
import { FileUploadError } from './FileUploadError';
import { FileUploadWarning } from './FileUploadWarning';

const DistributionListSectionContainer = styled.div`
	padding-top: 40px;
`;
DistributionListSectionContainer.displayName = 'DistributionListSectionContainer';

const DistributionListContainer = styled.div`
	text-align: left;
	margin-top: 50px;
`;
DistributionListContainer.displayName = 'DistributionListContainer';

const DistributionListButtonsContainer = styled.div`
	margin-bottom: 30px;
	& .btn {
		margin-right: 10px;
	}
	& .tws-button-group {
		align-items: start;
	}
`;
DistributionListButtonsContainer.displayName = 'DistributionListButtonsContainer';

export interface IDistributionListProps {
	surveyId: number;
	audienceId: number;
	accessCodeType?: AccessCodeType;
	distribution?: IDistributionFile;
	distributions?: IDistributionFile[];
	distributionListValidationErrors?: IValidationErrorCollection;
	distributionType: number;
	distributionValidationErrors?: IValidationErrorCollection;
	distributionValidationWarnings?: IValidationErrorCollection;
	externalApplicationType?: ExternalApplicationType;
	file?: any;
	fileUploadType?: DistributionFileUploadType;
	isDistributionLoading?: boolean;
	isTransferValidating?: boolean;
	isUploading: boolean;
	selectedDistribution?: IDistributionFile;
	ssoAttributeId?: SsoAttribute;
	AddDistribution: (file: any, distributionType?: number, deleteBeforeUpload?: boolean) => any; // Not used
	AppendDistribution: (file: any, distributionType?: number) => any;
	ClearValidation: () => any;
	RemoveDistributionFromList: () => any;
	DownloadDistributionTemplate: (distributionType: DistributionType, externalApplicationType?: ExternalApplicationType) => any;
	ValidateFile: (
		surveyId: number,
		audienceId: number,
		fileUploadType: DistributionFileUploadType,
		file: any,
		distributionType?: number,
		accessCode?: AccessCodeType,
		ssoAttribute?: SsoAttribute,
	) => any;
}
export type DistributionListProps = IDistributionListProps &
	IDistributionContactState &
	ISurveyParticipantState &
	IDistributionContactActionCreators &
	ISurveyParticipantActionCreators;

export class DistributionList extends React.Component<
	DistributionListProps,
	{
		appendModalOpen: boolean;
		warningModalOpen: boolean;
		distributionContactModalOpen: boolean;
	}
> {
	constructor(props: DistributionListProps) {
		super(props);

		this.state = {
			appendModalOpen: false,
			warningModalOpen: false,
			distributionContactModalOpen: false,
		};

		this.closeAppendModal = this.closeAppendModal.bind(this);
		this.closeWarningModal = this.closeWarningModal.bind(this);
		this.downloadTemplate = this.downloadTemplate.bind(this);
		this.onDelete = this.onDelete.bind(this);
		this.openAppendModal = this.openAppendModal.bind(this);
		this.openWarningModal = this.openWarningModal.bind(this);
		this.toggleDistributionContactsModal = this.toggleDistributionContactsModal.bind(this);
		this.uploadButtonChange = this.uploadButtonChange.bind(this);
	}

	public openAppendModal() {
		const { file } = this.props;
		if (!file) {
			this.setState({ appendModalOpen: true });
		}
	}

	public toggleDistributionContactsModal() {
		if (this.state.distributionContactModalOpen) {
			this.props.CloseDistributionContactModal();
			this.setState({ distributionContactModalOpen: false });
		} else {
			this.props.OpenDistributionContactModal();
			this.setState({ distributionContactModalOpen: true });
		}
	}

	public closeAppendModal() {
		const { ClearValidation } = this.props;
		this.setState({ appendModalOpen: false }, () => {
			if (ClearValidation) {
				ClearValidation();
			}
		});
	}

	public openWarningModal() {
		this.setState({ warningModalOpen: true });
	}

	public closeWarningModal() {
		this.setState({ warningModalOpen: false });
	}

	public uploadButtonChange(files: any) {
		const { audienceId, accessCodeType, distributionType, surveyId, ssoAttributeId, ValidateFile } = this.props;
		if (files && files.length > 0) {
			ValidateFile(surveyId, audienceId, DistributionFileUploadType.New, files[0], distributionType, accessCodeType, ssoAttributeId);
		}
	}

	public downloadTemplate() {
		this.props.DownloadDistributionTemplate(this.props.distributionType, this.props.externalApplicationType);
	}

	public render() {
		const { distributionType } = this.props;
		return this.getDisplayContent(distributionType);
	}

	private onDelete = () => {
		const { RemoveDistributionFromList } = this.props;
		this.setState({ warningModalOpen: false }, () => {
			if (RemoveDistributionFromList) {
				RemoveDistributionFromList();
			}
		});
	};

	private getContentBody(distribution?: IDistributionFile) {
		let contentBody = ContentConstants.WARNING_MODAL_DELETE_BODY;
		if (distribution) {
			contentBody = _.replace(contentBody, new RegExp('{name}', 'g'), distribution.fileName);
		}
		return contentBody;
	}

	private getDisplayContent(distributionType: DistributionType) {
		if (DistributionHelper.usesDistributionList(distributionType)) {
			return this.getFileUploadDistributionListContent();
		} else {
			return <></>;
		}
	}

	private distributionsInProgress() {
		if (!this.props.distributions) {
			return false;
		}

		return (
			this.props.distributions.filter((d: IDistributionFile) => d.distributionUploadStatusId === DistributionUploadStatus.InProgress)
				.length > 0
		);
	}

	private getFileUploadDistributionListContent() {
		const {
			distribution,
			distributions,
			distributionType,
			distributionListValidationErrors,
			distributionValidationWarnings,
			distributionValidationErrors,
			file,
			fileUploadType,
			isDistributionLoading,
			isUploading,
			selectedDistribution,
		} = this.props;
		const { appendModalOpen, warningModalOpen, distributionContactModalOpen } = this.state;
		const distributionListRequiredValidationErrorHelper = new ValidationErrorHelper<string>(distributionListValidationErrors);

		return (
			<>
				<Divider type='dashed' />
				<DistributionListSectionContainer>
					<Col3Col9 label={ContentConstants.DISTRIBUTIONLIST_TITLE} labelSize='lg' clearHeight='0' className='tws-distribution-list'>
						<p>{ContentConstants.INSTRUCTIONALTEXT_DISTRIBUTION_LIST_NOTE}</p>

						{distributionType == DistributionType.SingleLinkWithAccessCode && <AccessCodes />}

						<DistributionListContainer>
							{((distribution &&
								(distribution.action === IAction.Delete || distribution.distributionUploadStatusId === DistributionUploadStatus.Failed)) ||
								!distribution) &&
								!isDistributionLoading && (
									<DistributionListButtonsContainer>
										<ButtonGroup type='column' reversed={false}>
											<Button
												id='btnDownloadTemplate'
												size='sm'
												onClick={() =>
													this.props.DownloadDistributionTemplate(this.props.distributionType, this.props.externalApplicationType)
												}
											>
												{ContentConstants.BUTTONS_DOWNLOAD_TEMPLATE}
											</Button>
											<FileUpload
												btnSize={'sm'}
												btnTitle={ContentConstants.BUTTONS_UPLOAD_FILE}
												btnColor={'secondary'}
												getValue={this.uploadButtonChange}
												multiple={false}
												iconName='upload'
												fileNameLabelWidth={'250px'}
												files={file ? file.name : undefined}
												disabled={isUploading}
												error={distributionValidationErrors && fileUploadType === DistributionFileUploadType.New}
											/>
										</ButtonGroup>
									</DistributionListButtonsContainer>
								)}

							{distribution &&
								((distribution.action !== IAction.Delete &&
									(distribution.distributionUploadStatusId === DistributionUploadStatus.Completed ||
										!distribution.distributionUploadStatusId)) ||
									fileUploadType === DistributionFileUploadType.Append) &&
								!this.props.isTransferValidating &&
								!this.props.isDistributionLoading && (
									<Table responsive={true} lastalign='right'>
										<tbody>
											{
												<TableRow key={'distribution-row'}>
													<TableColumn>{distribution.fileName}</TableColumn>
													<TableColumn>{this.distributionsInProgress() ? '--' : distribution.contactsCount}</TableColumn>
													<TableColumn>{ContentConstants.SAVED}</TableColumn>
													<TableColumn>
														<ActionMenuWrapper>
															<DropdownMenuItem onClick={this.openAppendModal} disabled={this.isMenuItemDisabled()}>
																{ContentConstants.ACTIONMENU_APPENDLIST}
															</DropdownMenuItem>
															<DropdownMenuItem onClick={this.toggleDistributionContactsModal} disabled={this.isMenuItemDisabled()}>
																{ContentConstants.ACTIONMENU_VIEWEDIT}
															</DropdownMenuItem>
															<DropdownMenuItem onClick={this.openWarningModal} disabled={this.distributionsInProgress()}>
																{ContentConstants.ACTIONMENU_DELETE}
															</DropdownMenuItem>
														</ActionMenuWrapper>
													</TableColumn>
												</TableRow>
											}
										</tbody>
									</Table>
								)}

							{distributionValidationErrors && fileUploadType === DistributionFileUploadType.New && (
								<FileUploadError distributionValidationErrors={distributionValidationErrors} />
							)}
							{distributionValidationWarnings && <FileUploadWarning distributionValidationWarnings={distributionValidationWarnings} />}
							{!distributionListRequiredValidationErrorHelper.IsValid(DistributionListUnderValidation.DistributionList) && (
								<>
									<ErrorMessageLabel className='tws-distribution-list-error-message'>
										{distributionListRequiredValidationErrorHelper.Errors(DistributionListUnderValidation.DistributionList)}
									</ErrorMessageLabel>
								</>
							)}

							{(this.props.isTransferValidating || this.props.isDistributionLoading) && (
								<LoadingWrapper>
									<Loading height={'70px'} width={'70px'} />
								</LoadingWrapper>
							)}

							{!this.props.isDistributionLoading && (
								<DistributionUploadStatusMessage selectedDistribution={selectedDistribution} distributions={distributions} />
							)}

							{appendModalOpen && (
								<AppendDistributionModal
									instructionsText={ContentConstants.DISTRIBUTION_APPENDMODAL_INSTRUCTIONS}
									titleText={ContentConstants.DISTRIBUTION_APPENDMODAL_TITLE}
									distributionType={distributionType}
									distributionValidationErrors={distributionValidationErrors}
									file={this.props.file}
									fileUploadType={fileUploadType}
									isUploading={isUploading}
									modalOpen={this.state.appendModalOpen}
									validateOnly={true}
									closeAfterValidate={true}
									closeModal={this.closeAppendModal}
									downloadTemplate={this.downloadTemplate}
									validateFile={(fut: DistributionFileUploadType, f: any, dt?: number) =>
										this.props.ValidateFile(this.props.surveyId, this.props.audienceId, fut, f, dt)
									}
									showUploadButton={false}
									selectedDistribution={this.props.selectedDistribution}
								/>
							)}

							{warningModalOpen && (
								<WarningModal
									isOpen={warningModalOpen}
									onClose={() => this.closeWarningModal()}
									onSubmit={this.onDelete}
									contentHeadline={ContentConstants.WARNING_MODAL_DELETE_HEADLINE}
									contentBody={this.getContentBody(distribution)}
								/>
							)}
							{distributionContactModalOpen && (
								<DistributionContactsModal
									{...this.props}
									isOpen={this.state.distributionContactModalOpen}
									onClose={this.toggleDistributionContactsModal}
								/>
							)}
						</DistributionListContainer>
					</Col3Col9>
				</DistributionListSectionContainer>
			</>
		);
	}

	private isMenuItemDisabled() {
		return this.props.file || this.distributionsInProgress();
	}
}
